import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

function AuthCard({ title, subtitle, children }) {
  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'linear-gradient(150deg,#edfaf3 0%,#f5fdf9 60%,#fff 100%)',padding:24}}>
      <div style={{background:'#fff',borderRadius:20,padding:36,width:'100%',maxWidth:440,boxShadow:'0 20px 60px rgba(0,0,0,0.08)',border:'1.5px solid #deeee7'}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:28}}>
          <div style={{width:36,height:36,borderRadius:10,background:'linear-gradient(135deg,#22c97f,#0d6e4f)',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
            </svg>
          </div>
          <span style={{fontWeight:800,fontSize:'1.05rem',color:'#052e1c'}}>Budget<span style={{color:'#22c97f'}}>Tutor</span></span>
        </div>
        <h2 style={{marginBottom:4,fontSize:'1.5rem'}}>{title}</h2>
        {subtitle && <p style={{color:'var(--ink-400)',marginBottom:24,fontSize:'0.9rem'}}>{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [form, setForm]   = useState({ email:'', password:'' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const user = await login(form.email, form.password);
      if (user.role === 'admin')  navigate('/admin/dashboard');
      else if (user.role === 'tutor')  navigate('/tutor/dashboard');
      else navigate('/parent/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <AuthCard title="Welcome back" subtitle="Sign in to your account">
      <form onSubmit={handleSubmit}>
        {error && <div className="alert alert-error" style={{marginBottom:16}}>{error}</div>}
        <div className="form-group">
          <label className="form-label">Email address <span>*</span></label>
          <input type="email" className="form-input" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required autoFocus/>
        </div>
        <div className="form-group">
          <label className="form-label">Password <span>*</span></label>
          <input type="password" className="form-input" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required/>
        </div>
        <div style={{textAlign:'right',marginBottom:20}}>
          <Link to="/forgot-password" style={{fontSize:'0.82rem',color:'var(--green-600)'}}>Forgot password?</Link>
        </div>
        <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
          {loading ? <><span className="spinner" style={{width:18,height:18,borderWidth:2}}/> Signing in…</> : 'Sign in'}
        </button>
      </form>
      <div style={{marginTop:24,textAlign:'center',fontSize:'0.875rem',color:'var(--ink-400)'}}>
        New here? <Link to="/register/parent" style={{fontWeight:700,color:'var(--green-700)'}}>Create an account</Link>
      </div>
    </AuthCard>
  );
}

export function RegisterParent() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name:'', last_name:'', email:'', password:'', country:'GB' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const redirectTo = new URLSearchParams(window.location.search).get('redirect');

  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) return setError('Please accept the terms and conditions');
    setLoading(true); setError('');
    try {
      await register('parent', form);
      navigate(redirectTo || '/parent/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <AuthCard title="Create your account" subtitle="Find affordable tutors today">
      <form onSubmit={handleSubmit}>
        {error && <div className="alert alert-error" style={{marginBottom:16}}>{error}</div>}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">First name <span>*</span></label>
            <input type="text" className="form-input" value={form.first_name} onChange={e=>setForm({...form,first_name:e.target.value})} required autoFocus/>
          </div>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">Last name <span>*</span></label>
            <input type="text" className="form-input" value={form.last_name} onChange={e=>setForm({...form,last_name:e.target.value})} required/>
          </div>
        </div>
        <div className="form-group" style={{marginTop:12}}>
          <label className="form-label">Email address <span>*</span></label>
          <input type="email" className="form-input" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required/>
        </div>
        <div className="form-group">
          <label className="form-label">Password <span>*</span></label>
          <input type="password" className="form-input" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required minLength={8}/>
        </div>
        <div className="form-group">
          <label className="form-label">Country <span>*</span></label>
          <select className="form-select" value={form.country} onChange={e=>setForm({...form,country:e.target.value})}>
            <option value="GB">🇬🇧 United Kingdom (GBP £)</option>
            <option value="US">🇺🇸 United States (USD $)</option>
          </select>
        </div>
        <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading} style={{marginTop:8}}>
          {loading ? 'Creating account…' : 'Create account — it\'s free'}
        </button>
      </form>
      <div style={{marginTop:20,textAlign:'center',fontSize:'0.82rem',color:'var(--ink-400)'}}>
        Already have an account? <Link to="/login" style={{fontWeight:700,color:'var(--green-700)'}}>Sign in</Link>
      </div>
      <div style={{marginTop:12,textAlign:'center',fontSize:'0.82rem',color:'var(--ink-400)'}}>
        Want to tutor? <Link to="/register/tutor" style={{fontWeight:700,color:'var(--green-700)'}}>Apply here</Link>
      </div>
    </AuthCard>
  );
}

export function RegisterTutor() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name:'', last_name:'', email:'', password:'', country:'GB' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await register('tutor', form);
      navigate('/tutor/onboarding');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <AuthCard title="Apply as a tutor" subtitle="One-off £1/$1 platform access fee">
      <div style={{background:'var(--green-50)',border:'1.5px solid var(--green-200)',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:'0.82rem',color:'var(--green-800)'}}>
        <strong>How it works:</strong> Create your account, pay the £1/$1 non-refundable access fee, then complete our 11-step onboarding process. Our team reviews applications within 48 hours.
      </div>
      <form onSubmit={handleSubmit}>
        {error && <div className="alert alert-error" style={{marginBottom:16}}>{error}</div>}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">First name <span>*</span></label>
            <input type="text" className="form-input" value={form.first_name} onChange={e=>setForm({...form,first_name:e.target.value})} required autoFocus/>
          </div>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">Last name <span>*</span></label>
            <input type="text" className="form-input" value={form.last_name} onChange={e=>setForm({...form,last_name:e.target.value})} required/>
          </div>
        </div>
        <div className="form-group" style={{marginTop:12}}>
          <label className="form-label">Email address <span>*</span></label>
          <input type="email" className="form-input" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required/>
        </div>
        <div className="form-group">
          <label className="form-label">Password <span>*</span></label>
          <input type="password" className="form-input" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required minLength={8}/>
        </div>
        <div className="form-group">
          <label className="form-label">Country <span>*</span></label>
          <select className="form-select" value={form.country} onChange={e=>setForm({...form,country:e.target.value})}>
            <option value="GB">🇬🇧 United Kingdom</option>
            <option value="US">🇺🇸 United States</option>
          </select>
        </div>
        <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading} style={{marginTop:8}}>
          {loading ? 'Creating account…' : 'Create account & continue'}
        </button>
      </form>
      <div style={{marginTop:20,textAlign:'center',fontSize:'0.82rem',color:'var(--ink-400)'}}>
        Already have an account? <Link to="/login" style={{fontWeight:700,color:'var(--green-700)'}}>Sign in</Link>
      </div>
    </AuthCard>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent]   = useState(false);
  const [loading, setLoading] = useState(false);
  const api = require('../../utils/api').default;

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    await api.post('/auth/forgot-password', { email }).catch(()=>{});
    setSent(true); setLoading(false);
  };

  return (
    <AuthCard title="Reset password" subtitle="We'll send you a reset link">
      {sent
        ? <div className="alert alert-success">If that email exists, a reset link has been sent. Check your inbox.</div>
        : <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input type="email" className="form-input" value={email} onChange={e=>setEmail(e.target.value)} required autoFocus/>
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>{loading ? 'Sending…' : 'Send reset link'}</button>
          </form>
      }
      <div style={{marginTop:20,textAlign:'center',fontSize:'0.875rem'}}>
        <Link to="/login" style={{color:'var(--green-600)',fontWeight:600}}>← Back to sign in</Link>
      </div>
    </AuthCard>
  );
}

export function ResetPassword() {
  const { token } = require('react-router-dom').useParams();
  const navigate  = require('react-router-dom').useNavigate();
  const api       = require('../../utils/api').default;
  const [password, setPassword] = useState('');
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed');
    }
    setLoading(false);
  };

  return (
    <AuthCard title="Choose new password">
      {done
        ? <div className="alert alert-success">Password reset! Redirecting…</div>
        : <form onSubmit={handleSubmit}>
            {error && <div className="alert alert-error" style={{marginBottom:16}}>{error}</div>}
            <div className="form-group">
              <label className="form-label">New password</label>
              <input type="password" className="form-input" value={password} onChange={e=>setPassword(e.target.value)} required minLength={8} autoFocus/>
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>{loading ? 'Saving…' : 'Set new password'}</button>
          </form>
      }
    </AuthCard>
  );
}

export default Login;
